import { usePostHog } from '@posthog/react';
import PlusIcon from 'assets/images/icons/PlusIcon.svg?react';
import { useCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { isPlainLeftClick } from 'components/global-search/GlobalSearchRow';
import { Button } from 'components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import { FormattedMessage } from 'react-intl';
import { type GlobalAddItem, useGlobalAddItems } from './useGlobalAddItems';

/**
 * The add menu's entries, shared by the floating + and the new design's New. Each is a link to its create page.
 * Where the create dialog exists a plain click on a creation asks the dialog for it instead, over the current
 * page: it opens there, unless the user keeps a creation something was typed in. The link is still what a new
 * tab takes, and what an entry that creates nothing follows.
 */
export function GlobalAddMenuItems({ items }: { items: GlobalAddItem[] }) {
    const posthog = usePostHog();
    const createDialog = useCreateDialog();
    return items.map((item) => (
        <DropdownMenuItem
            key={item.label}
            render={
                <LinkWithOrigin
                    to={item.url}
                    disableReturnUrlStateSetting
                    onClick={(event) => {
                        if (isPlainLeftClick(event) && createDialog?.open(item.url)) {
                            event.preventDefault();
                        }
                    }}
                />
            }
            onClick={() => posthog.capture('global_add_item_selected', { item: item.label })}
        >
            {item.label}
        </DropdownMenuItem>
    ));
}

export default function GlobalAddButton() {
    const posthog = usePostHog();
    const items = useGlobalAddItems();

    if (items.length === 0) {
        return null;
    }

    return (
        <DropdownMenu
            onOpenChange={(open) => {
                if (open) posthog.capture('global_add_menu_opened');
            }}
        >
            <DropdownMenuTrigger
                render={
                    <Button
                        variant="secondary"
                        aria-label="add"
                        // Under the export ribbon, which shares this corner: over it, the button would cover the
                        // ribbon's own minimise and close controls.
                        className="fixed right-[calc(1.5rem+var(--ai-side-dock-width,0px))] bottom-6 z-[calc(var(--z-floating-ai-launcher)-2)] size-[62px] rounded-full shadow-lg transition-transform duration-250 motion-safe:hover:scale-[1.0645] [&_svg]:transition-transform [&_svg]:duration-250 motion-safe:hover:[&_svg]:scale-[0.9394] [&_svg:not([class*='size-'])]:size-auto"
                    >
                        <PlusIcon />
                    </Button>
                }
            />
            <DropdownMenuContent side="top" align="end" className="w-auto min-w-[216px]">
                <div className="text-h6 mx-4 font-semibold">
                    <FormattedMessage id="add-new" />
                </div>
                <DropdownMenuSeparator className="my-2" />
                <GlobalAddMenuItems items={items} />
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
